import { signInWithEmailAndPassword } from "firebase/auth";
import React, { FC, useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Link,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import LoadingScreen from "../../components/LoadingScreen";
import SaveButton from "../../components/SaveButton";
import { ViewIcon } from "../../components/icons";
import TextField from "../../components/styled/TextField";
import { firebaseAuth } from "../../configs/firebase";
import { useDialog } from "../../hooks/use-dialog";
import { useSettings } from "../../hooks/use-settings";
import useThirdPartyLogin from "../../hooks/use-third-party-login";
import GithubIcon from "../../icons/githubIcon";
import GoogleIcon from "../../icons/googleIcon";
import { parseErrorCode } from "../../utils/firebaseErrorParser";
import LoginShell from "./LoginShell";
import RequestPasswordReset, {
  RequestPasswordResetParams,
} from "./RequestPasswordReset";

const Login: FC = () => {
  const navigate = useNavigate();
  const settings = useSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [user, loading] = useAuthState(firebaseAuth);
  const passwordResetDialogHandlers = useDialog<RequestPasswordResetParams>();
  const [pageError, setPageError] = useState<string>("");
  const {
    handleGoogleLogin,
    handleGithubLogin,
    isLoading: thirdPartyLoginLoading,
    error: thirdPartyLoginError,
  } = useThirdPartyLogin();

  const handleLogin = async () => {
    try {
      const cred = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );
      if (import.meta.env.PROD) {
        //syft
        window?.syft?.identify(cred?.user?.email || "", {
          firstName: cred?.user?.displayName || "",
          type: "Email Login",
        });
        //commonroom
        window?.signals?.identify({
          email: cred?.user?.email || "",
          name: cred?.user?.displayName || "",
        });
      }
    } catch (e) {
      setPageError(parseErrorCode(e));
    }
  };

  useEffect(() => {
    setPageError(parseErrorCode(thirdPartyLoginError));
  }, [thirdPartyLoginError]);

  if (loading || user || thirdPartyLoginLoading) {
    return <LoadingScreen />;
  }

  document.title = "Login";
  return (
    <LoginShell>
      <Card
        sx={{
          boxShadow: 5,
        }}
      >
        <CardContent>
          <RequestPasswordReset dialog={passwordResetDialogHandlers} />
          <form>
            <Stack spacing={2} sx={{ mt: 5, px: 5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h3">Login</Typography>
                <Link
                  sx={{ cursor: "pointer" }}
                  color="primary"
                  onClick={() => navigate("/create-account")}
                  underline="none"
                  variant="subtitle2"
                >
                  Sign up
                </Link>
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Email</Typography>
                <TextField
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                />
              </Stack>
              <Stack>
                <Typography variant="subtitle2">Password</Typography>
                <TextField
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ViewIcon
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              {!!pageError && (
                <Stack direction="row" justifyContent="center">
                  <Alert severity="error" sx={{ maxWidth: "300px" }}>
                    {pageError}
                  </Alert>
                </Stack>
              )}
              <Stack direction="row" justifyContent="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ maxWidth: "300px" }}>
                  By clicking the buttons below, you acknowledge that you have
                  read, understood, and agree to Pomerium's{" "}
                  <Link
                    sx={{ cursor: "pointer" }}
                    color="primary"
                    underline="none"
                    variant="subtitle2"
                    href="https://www.pomerium.com/zero-terms-and-conditions/"
                    target="_blank"
                  >
                    terms of service
                  </Link>{" "}
                  and{" "}
                  <Link
                    sx={{ cursor: "pointer" }}
                    color="primary"
                    underline="none"
                    variant="subtitle2"
                    href="https://www.pomerium.com/privacy-policy"
                    target="_blank"
                  >
                    privacy policy
                  </Link>
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                sx={{ pt: 2 }}
                spacing={1}
              >
                <SaveButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: 300 }}
                  onClick={handleLogin}
                  disabled={!email || !password}
                >
                  Login
                </SaveButton>
              </Stack>
            </Stack>
          </form>
          <Stack
            direction="row"
            justifyContent="center"
            sx={{ pb: 2, pt: 2 }}
            spacing={1}
          >
            <Link
              color="primary"
              sx={{ cursor: "pointer" }}
              underline="none"
              variant="body2"
              onClick={() =>
                passwordResetDialogHandlers.handleOpen({
                  email,
                })
              }
            >
              Forgot password
            </Link>
          </Stack>
          <Stack direction="row" justifyContent="center" spacing={1}>
            <IconButton size="small" onClick={handleGoogleLogin}>
              <GoogleIcon />
            </IconButton>
            <IconButton size="small" onClick={handleGithubLogin}>
              <GithubIcon
                fill={settings.paletteMode === "dark" ? "#c5d1de" : "#000"}
              />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            justifyContent="center"
            spacing={1}
            sx={{ pt: 1 }}
          ></Stack>
          <Box mt={5} />
        </CardContent>
      </Card>
    </LoginShell>
  );
};

export default Login;
